import { BrowserRouter, Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';

import { Layout } from './components/Layout';
import { ProtectedRoute } from './components/ProtectedRoute';
import { getEnv } from './lib/env';
import { ActivityLogPage } from './pages/activity-log/ActivityLogPage';
import { AgentDefinitionsPage } from './pages/agent-definitions/List';
import { AgentExecutionDetailPage } from './pages/agent-executions/ExecutionDetail';
import { AgentExecutionsListPage } from './pages/agent-executions/ExecutionsList';
import { JudgeTuningEditPage } from './pages/agent-improvement/judge-tuning/Edit';
import { JudgeTuningListPage } from './pages/agent-improvement/judge-tuning/List';
import { OverviewPage } from './pages/agent-improvement/overview/OverviewPage';
import { PipelineRunDetailPage } from './pages/agent-improvement/pipeline/RunDetail';
import { PipelineRunsListPage } from './pages/agent-improvement/pipeline/RunsList';
import { ReplayDetailPage } from './pages/agent-improvement/replays/Detail';
import { ReplaysListPage } from './pages/agent-improvement/replays/List';
import { AnalystReportDetailPage } from './pages/ai-analyst/Detail';
import { AnalystReportsListPage } from './pages/ai-analyst/List';
import { AvailabilityLeadsPage } from './pages/availability-leads/List';
import { AvailabilityDashboardPage } from './pages/AvailabilityDashboard';
import BookingsPage from './pages/bookings/BookingsPage';
import { CallQualityDashboardPage } from './pages/CallQualityDashboard';
import { CallDetailPage } from './pages/calls/Detail';
import { CallsPage } from './pages/calls/List';
import { CampaignCreatePage } from './pages/campaigns/Create';
import { CampaignDetailPage } from './pages/campaigns/Detail';
import { CampaignsPage } from './pages/campaigns/List';
import { ChatbotPage } from './pages/Chatbot';
import { ChatbotToolsPage } from './pages/chatbot-tools/List';
import { ClassifierConfigEditPage } from './pages/classifier-configs/Edit';
import { ClassifierConfigsListPage } from './pages/classifier-configs/List';
import { CompareKpisPage } from './pages/compare-kpis/CompareKpisPage';
import { ConfigurationEditPage } from './pages/configurations/Edit';
import { ConfigurationSetupsPage } from './pages/ConfigurationSetups';
import { ConfiguredDashboardPage } from './pages/ConfiguredDashboard';
import { ContactsPage } from './pages/contacts/ContactsPage';
import { CustomToolsPage } from './pages/custom-tools/List';
import { DashboardConfigEditPage } from './pages/dashboard-configs/Edit';
import { DashboardConfigsPage } from './pages/dashboard-configs/List';
import { DealerKitContactsPage } from './pages/dealer-kit/Contacts';
import { DealerKitRegistrationsPage } from './pages/dealer-kit/Registrations';
import { DealerKitStockPage } from './pages/dealer-kit/Stock';
import FinanceApplicationsPage from './pages/finance-applications/FinanceApplicationsPage';
import { HomePage } from './pages/Home';
import { LeadDetailPage } from './pages/leads/Detail';
import { LeadsPage } from './pages/leads/List';
import { LoginPage } from './pages/Login';
import { MessagingPage } from './pages/messaging/Messaging';
import { MetricDefinitionEditPage } from './pages/metric-definitions/Edit';
import { MetricDefinitionsListPage } from './pages/metric-definitions/List';
import { MisclassifiedCallEditPage } from './pages/misclassified-calls/Edit';
import { MisclassifiedCallsListPage } from './pages/misclassified-calls/List';
import { OutcomesPage } from './pages/outcomes/Outcomes';
import { PaymentsPage } from './pages/payments/List';
import { PlatformCostsPage } from './pages/PlatformCosts';
import PublicDocumentPage from './pages/PublicDocument';
import { QualityConfigEditPage } from './pages/quality-configs/Edit';
import { QualityConfigsListPage } from './pages/quality-configs/List';
import { RedisPage } from './pages/Redis';
import { ReportsListPage } from './pages/reports/List';
import { CallReviewsPage } from './pages/reviews/CallReviews';
import { CampaignReviewsPage } from './pages/reviews/CampaignReviews';
import { ReviewHistoryPage } from './pages/reviews/History';
import { ReviewConfigsPage } from './pages/reviews/ReviewConfigs';
import { ReviewDetailPage } from './pages/reviews/ReviewDetail';
import { CampaignReviewHistoryPage } from './pages/reviews/ReviewHistory';
import ScheduledMessagesPage from './pages/scheduled-messages/ScheduledMessagesPage';
import { ScrapersPage } from './pages/scrapers/List';
import { SettingsPage } from './pages/Settings';
import { ShiftConfirmationsPage } from './pages/shift-confirmations/List';
import { SignaturesPage } from './pages/signatures/List';
import { SmsActionConfigsPage } from './pages/sms-actions/SmsActionConfigs';
import { StockSourceDetailPage } from './pages/stock-sync/Detail';
import { StockSourcesPage } from './pages/stock-sync/List';
import { TelnyxNumbersPage } from './pages/TelnyxNumbers';
import { TwilioCostsPage } from './pages/TwilioCosts';
import { TwilioNumbersPage } from './pages/TwilioNumbers';
import { UsersPage } from './pages/Users';
import { WebhooksPage } from './pages/webhooks/List';
import { WhatsAppNumbersPage } from './pages/WhatsAppNumbers';
import { WhatsAppTemplatesPage } from './pages/WhatsAppTemplates';
import { WidgetDemoPage } from './pages/WidgetDemo';
import { WorkflowTemplatesPage } from './pages/workflow-templates/List';
import { BatchRunsPage } from './pages/workflows/BatchRuns';
import { ExecutionDetailPage } from './pages/workflows/ExecutionDetail';
import { ExecutionHistoryPage } from './pages/workflows/ExecutionHistory';
import { WorkflowsPage } from './pages/workflows/List';
import { useAuthStore } from './stores/auth.store';
import { useFeaturesStore } from './stores/features.store';

/** /contacts moved to /sales-console — forward old links, keeping id and query. */
const LegacyContactsRedirect = () => {
  const { contactId } = useParams<{ contactId: string }>();
  const { search } = useLocation();
  return <Navigate to={`/sales-console${contactId ? `/${contactId}` : ''}${search}`} replace />;
};

const App = () => {
  const userRole = useAuthStore((s) => s.user?.role);
  const {
    chatbot: featureChatbot,
    gcpCosts: featureGcpCosts,
    twilioCosts: featureTwilioCosts,
    smsActionsModule: featureSmsActionsModule,
    reports: featureReports,
    webhooks: featureWebhooks,
    paymentLinks: featurePaymentLinks,
    digitalSign: featureDigitalSign,
    dealerKit: featureDealerKit,
    stockSync: featureStockSync,
    messaging: featureMessaging,
    agentImprovement: featureAgentImprovement,
    compareKpis: featureCompareKpis,
    analyticsReporter: featureAnalyticsReporter,
  } = useFeaturesStore();

  // Clients view: 'client' role users only get the (read-only) calls pages.
  // Enforced regardless of FEATURE_CLIENTS_VIEW to mirror the backend, which
  // always restricts the role.
  if (userRole === 'client') {
    return (
      <BrowserRouter>
        <Routes>
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <Layout />
              </ProtectedRoute>
            }
          >
            <Route index element={<Navigate to="/calls" replace />} />
            <Route path="calls" element={<CallsPage />} />
            <Route path="calls/:callId" element={<CallDetailPage />} />
          </Route>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/d/:token" element={<PublicDocumentPage />} />
          <Route path="*" element={<Navigate to="/calls" replace />} />
        </Routes>
      </BrowserRouter>
    );
  }

  return (
    <BrowserRouter>
      <Routes>
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <Layout />
            </ProtectedRoute>
          }
        >
          <Route index element={<HomePage />} />
          <Route path="analytics" element={<ConfiguredDashboardPage />} />
          <Route path="call-quality" element={<CallQualityDashboardPage />} />
          <Route path="users" element={<UsersPage />} />
          <Route path="activity-log" element={<ActivityLogPage />} />
          <Route path="settings" element={<SettingsPage />} />
          <Route path="dashboard-configs" element={<DashboardConfigsPage />} />
          <Route path="dashboard-configs/:id" element={<DashboardConfigEditPage />} />
          <Route path="metric-definitions" element={<MetricDefinitionsListPage />} />
          <Route path="outcomes" element={<OutcomesPage />} />
          <Route path="metric-definitions/:key" element={<MetricDefinitionEditPage />} />
          <Route path="classifier-configs" element={<ClassifierConfigsListPage />} />
          <Route path="classifier-configs/:id" element={<ClassifierConfigEditPage />} />
          <Route path="quality-configs" element={<QualityConfigsListPage />} />
          <Route path="quality-configs/:id" element={<QualityConfigEditPage />} />
          <Route path="quality-executions" element={<AgentExecutionsListPage kind="quality" />} />
          <Route
            path="quality-executions/:callId/:name"
            element={<AgentExecutionDetailPage kind="quality" />}
          />
          <Route
            path="classifier-executions"
            element={<AgentExecutionsListPage kind="classification" />}
          />
          <Route
            path="classifier-executions/:callId/:name"
            element={<AgentExecutionDetailPage kind="classification" />}
          />
          {featureAgentImprovement && <Route path="agent-improvement" element={<OverviewPage />} />}
          {featureAgentImprovement && (
            <Route path="agent-improvement/judge-tuning" element={<JudgeTuningListPage />} />
          )}
          {featureAgentImprovement && (
            <Route path="agent-improvement/judge-tuning/:id" element={<JudgeTuningEditPage />} />
          )}
          {featureAgentImprovement && (
            <Route path="agent-improvement/replays" element={<ReplaysListPage />} />
          )}
          {featureAgentImprovement && (
            <Route path="agent-improvement/replays/:id" element={<ReplayDetailPage />} />
          )}
          {featureAgentImprovement && (
            <Route path="agent-improvement/runs" element={<PipelineRunsListPage />} />
          )}
          {featureAgentImprovement && (
            <Route path="agent-improvement/runs/:id" element={<PipelineRunDetailPage />} />
          )}
          {featureAgentImprovement && (
            <Route path="misclassified-calls" element={<MisclassifiedCallsListPage />} />
          )}
          {featureAgentImprovement && (
            <Route path="misclassified-calls/:id" element={<MisclassifiedCallEditPage />} />
          )}
          <Route path="configurations" element={<ConfigurationSetupsPage />} />
          <Route path="configurations/:id" element={<ConfigurationEditPage />} />
          <Route path="agent-definitions" element={<AgentDefinitionsPage />} />
          <Route path="custom-tools" element={<CustomToolsPage />} />
          <Route path="scrapers" element={<ScrapersPage />} />

          <Route path="campaigns" element={<CampaignsPage />} />
          <Route path="campaigns/new" element={<CampaignCreatePage />} />
          <Route path="campaigns/:id" element={<CampaignDetailPage />} />
          <Route path="sales-console/:contactId?" element={<ContactsPage />} />
          {/* Old bookmarks and shared links keep working */}
          <Route path="contacts/:contactId?" element={<LegacyContactsRedirect />} />
          <Route path="calls" element={<CallsPage />} />
          <Route path="calls/:callId" element={<CallDetailPage />} />
          <Route path="leads" element={<LeadsPage />} />
          <Route path="leads/:leadId" element={<LeadDetailPage />} />
          {featureGcpCosts && <Route path="platform-costs" element={<PlatformCostsPage />} />}
          {featureTwilioCosts && <Route path="twilio-costs" element={<TwilioCostsPage />} />}
          <Route path="twilio-numbers" element={<TwilioNumbersPage />} />
          <Route path="telnyx-numbers" element={<TelnyxNumbersPage />} />
          <Route path="whatsapp-numbers" element={<WhatsAppNumbersPage />} />
          <Route path="whatsapp-templates" element={<WhatsAppTemplatesPage />} />
          {featureReports && <Route path="reports" element={<ReportsListPage />} />}
          {getEnv('VITE_CLIENT_COMPANY_NAME_INTERNAL') === 'NEW_CROSS' && (
            <Route path="availability-dashboard" element={<AvailabilityDashboardPage />} />
          )}
          {getEnv('VITE_CLIENT_COMPANY_NAME_INTERNAL') === 'NEW_CROSS' && (
            <Route path="availability-leads" element={<AvailabilityLeadsPage />} />
          )}
          {getEnv('VITE_CLIENT_COMPANY_NAME_INTERNAL') === 'ROYALTY_STAFFING' && (
            <Route path="shift-confirmations" element={<ShiftConfirmationsPage />} />
          )}
          <Route path="reviews" element={<CampaignReviewsPage />} />
          <Route path="reviews/campaigns" element={<CampaignReviewsPage />} />
          <Route path="reviews/calls" element={<CallReviewsPage />} />
          <Route path="reviews/configs" element={<ReviewConfigsPage />} />
          <Route path="workflow-templates" element={<WorkflowTemplatesPage />} />
          <Route path="workflows" element={<WorkflowsPage />} />
          <Route path="workflows/history" element={<ExecutionHistoryPage />} />
          <Route path="workflows/history/:id" element={<ExecutionDetailPage />} />
          <Route path="workflows/:id" element={<WorkflowsPage />} />
          <Route path="workflows/:id/batch-runs" element={<BatchRunsPage />} />
          {featureCompareKpis && <Route path="compare-kpis" element={<CompareKpisPage />} />}
          {featureAnalyticsReporter && (
            <Route path="ai-analyst" element={<AnalystReportsListPage />} />
          )}
          {featureAnalyticsReporter && (
            <Route path="ai-analyst/:id" element={<AnalystReportDetailPage />} />
          )}
          <Route path="bookings" element={<BookingsPage />} />
          <Route path="scheduled-sends" element={<ScheduledMessagesPage />} />
          <Route path="finance-applications" element={<FinanceApplicationsPage />} />
          <Route path="reviews/history" element={<ReviewHistoryPage />} />
          <Route path="reviews/call/:callId" element={<ReviewDetailPage />} />
          <Route path="reviews/campaign-review/:campaignReviewId" element={<ReviewDetailPage />} />
          <Route
            path="reviews/campaign/:campaignId/history"
            element={<CampaignReviewHistoryPage />}
          />
          {featureSmsActionsModule && (
            <Route path="sms-actions" element={<SmsActionConfigsPage />} />
          )}
          {featurePaymentLinks && <Route path="payments" element={<PaymentsPage />} />}
          {featureDigitalSign && <Route path="signatures" element={<SignaturesPage />} />}
          {featureWebhooks && <Route path="webhooks" element={<WebhooksPage />} />}
          {featureDealerKit && <Route path="dealer-kit" element={<DealerKitRegistrationsPage />} />}
          {featureDealerKit && (
            <Route path="dealer-kit/contacts" element={<DealerKitContactsPage />} />
          )}
          {featureDealerKit && <Route path="dealer-kit/stock" element={<DealerKitStockPage />} />}
          {featureStockSync && <Route path="stock-sync" element={<StockSourcesPage />} />}
          {featureStockSync && <Route path="stock-sync/:id" element={<StockSourceDetailPage />} />}
          {featureMessaging && (
            <Route path="messaging/:conversationId?" element={<MessagingPage />} />
          )}
          {featureChatbot && <Route path="chatbot/tools" element={<ChatbotToolsPage />} />}
          {featureChatbot && <Route path="chatbot/:chatId?" element={<ChatbotPage />} />}
          {process.env.NODE_ENV === 'development' && <Route path="redis" element={<RedisPage />} />}
        </Route>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/d/:token" element={<PublicDocumentPage />} />
        <Route path="/widget-demo/:configId" element={<WidgetDemoPage />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
};

export default App;
